import classNames from "classnames";
import { QuestionMarkIcon } from "design_system";
import { INTERCOM_CUSTOM_LAUNCHER_SELECTOR } from "helpers/services/intercom";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

interface IntercomButtonProps {
  className?: string;
}

/**
 * Custom Intercom launcher button that replaces the default Intercom bubble.
 * Uses the id="intercom_launcher_link" which Intercom's custom_launcher_selector targets.
 * Shows unread message count as a badge.
 * Parent controls visibility via className.
 *
 * @example
 * // Default usage
 * <IntercomButton />
 *
 * @example
 * // Hidden on narrow containers (Thread page)
 * <IntercomButton className="hidden @[1040px]:flex" />
 */
export default function IntercomButton({ className }: IntercomButtonProps) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined" || !window.Intercom) {
      return;
    }

    window.Intercom("onUnreadCountChange", (count: number) => {
      setUnreadCount(count);
    });

    window.Intercom("onShow", () => {
      setIsMessagesOpen(true);
    });

    window.Intercom("onHide", () => {
      setIsMessagesOpen(false);
    });
  }, []);

  return (
    <button
      id={INTERCOM_CUSTOM_LAUNCHER_SELECTOR}
      className={classNames(
        // Base styles with fixed positioning
        "fixed bottom-5 right-5 z-40",
        "bg-bg-faint hover:bg-bg-muted text-fg-base rounded-full border transition-all",
        "flex items-center justify-center",
        "w-8 h-8",

        // Parent controls visibility via className
        className
      )}
      aria-label="Open support chat"
    >
      {isMessagesOpen ? <X size={16} /> : <QuestionMarkIcon size="sm" />}

      {/* Unread count badge */}
      {unreadCount > 0 && (
        <div className="absolute -top-[2px] -right-[2px] flex items-center justify-center w-[10px] h-[10px] bg-chart-red-base rounded-full" />
      )}
    </button>
  );
}
